import { useEffect, useState } from "react";
import { LogOut } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api, fmtErr } from "@/lib/api";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminKndlaReport from "@/pages/admin/AdminKndlaReport";
import { AdminLanguageProvider, useAdminLanguage } from "@/i18n/AdminLanguage";

const TABS = [
  { id: "sales", label: "Sales" },
  { id: "promoters", label: "Promoters" },
  { id: "events", label: "Events" },
  { id: "settings", label: "Settings" },
];

export default function Admin() {
  return <AdminLanguageProvider><AdminBody/></AdminLanguageProvider>;
}

function AdminBody() {
  const {tr:t, lang, setLang}=useAdminLanguage();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("sales");

  useEffect(() => {
    if (!localStorage.getItem("kndla_token")) {
      setChecking(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("kndla_token"))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return <div className="grid min-h-screen place-items-center bg-coal font-manrope text-sm text-white/40">{t("Loading…")}</div>;
  }
  if (!user) return <AdminLogin onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-coal font-manrope text-white">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ash/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
          <div className="flex items-center gap-3">
            <Logo testId="admin-logo" />
            <span className="rounded bg-flame/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-flame">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-xs text-white/40 sm:block">{user.email}</span>
            <label className="flex items-center gap-2 text-xs text-white/60">
              <span className="sr-only">{t("Language")}</span>
              <select aria-label={t("Language")} className="rounded-lg border border-white/20 bg-coal px-2 py-1.5 text-white" value={lang} onChange={e=>setLang(e.target.value)}>
                <option value="es">ES</option><option value="en">EN</option><option value="ru">RU</option>
              </select>
            </label>
            <button
              data-testid="admin-logout-btn"
              onClick={() => {
                localStorage.removeItem("kndla_token");
                setUser(null);
              }}
              className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold transition-colors hover:border-flame hover:text-flame"
            >
              <LogOut className="h-3.5 w-3.5" /> {t("Log out")}
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-8">
        <nav className="flex flex-wrap gap-2">
          {TABS.map((tabItem) => (
            <button
              key={tabItem.id}
              data-testid={`admin-tab-${tabItem.id}`}
              onClick={() => setTab(tabItem.id)}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-all duration-300 ${
                tab === tabItem.id
                  ? "flame-glow bg-flame text-white"
                  : "border border-white/15 text-white/55 hover:border-white/40 hover:text-white"
              }`}
            >
              {t(tabItem.label)}
            </button>
          ))}
        </nav>
        <div className="mt-8">
          
{tab === "sales" && <AdminKndlaReport view="sales" />}
          {tab === "promoters" && <AdminKndlaReport view="promoters" />}
          {tab === "events" && <AdminEvents />}
          {tab === "settings" && <AdminSettings />}
        </div>
      </div>
    </div>
  );
}

const loginInputCls =
  "mt-1.5 w-full rounded-lg border border-white/15 bg-coal px-3.5 py-2.5 text-sm text-white outline-none transition-colors placeholder:text-white/25 focus:border-flame";

function AdminLogin({ onLogin }) {
  const {tr:t,lang,setLang}=useAdminLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("kndla_token", data.token);
      onLogin(data.user);
    } catch (err) {
      setError(fmtErr(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-coal px-5 font-manrope">
      <div className="grain-overlay" aria-hidden="true" />
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border border-white/10 bg-ash p-8"
      >
        <div className="flex justify-center">
          <Logo testId="admin-login-logo" />
        </div>
        <h1 className="mt-6 text-center font-syne text-2xl font-extrabold tracking-tight text-white">{t("Back office")}</h1>
        <p className="mt-1 text-center text-xs text-white/40">{t("Staff only")}</p>
        {error && (
          <p data-testid="admin-login-error" className="mt-4 rounded-lg border border-flame/40 bg-flame/10 px-3 py-2 text-xs text-flame">
            {error}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2 text-xs text-white/50"><span>{t("Language")}</span><select aria-label={t("Language")} value={lang} onChange={e=>setLang(e.target.value)} className="rounded border border-white/20 bg-coal text-white"><option value="es">ES</option><option value="en">EN</option><option value="ru">RU</option></select></div>
        <label className="mt-6 block text-[10px] font-bold uppercase tracking-[0.25em] text-white/50">Email</label>
        <input
          data-testid="admin-email-input"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@kndla.com"
          className={loginInputCls}
        />
        <label className="mt-4 block text-[10px] font-bold uppercase tracking-[0.25em] text-white/50">{t("Password")}</label>
        <input
          data-testid="admin-password-input"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className={loginInputCls}
        />
        <button
          data-testid="admin-login-submit"
          disabled={busy}
          className="flame-glow mt-6 w-full rounded-full bg-flame py-3 text-sm font-bold text-white transition-colors hover:bg-ember disabled:opacity-50"
        >
          {busy ? t("Checking…") : t("Log in")}
        </button>
      </form>
    </div>
  );
}
